import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Loader2, Check } from "lucide-react";

export default function ConfirmDialog({ open, title, description, confirmLabel = "Delete", danger = true, onConfirm, onClose }) {
  const [state, setState] = useState("idle"); // idle | loading | done

  const run = async () => {
    setState("loading");
    try {
      await onConfirm();
      setState("done");
      try { navigator.vibrate?.([10, 30, 10]); } catch {}
      setTimeout(() => { setState("idle"); onClose(); }, 900);
    } catch {
      setState("idle");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[90] flex items-center justify-center px-8"
                    style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={() => state === "idle" && onClose()}>
          <motion.div onClick={(e) => e.stopPropagation()} data-testid="confirm-dialog"
                      initial={{ scale: 0.8, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ scale: 0.85, opacity: 0 }} transition={{ type: "spring", stiffness: 320, damping: 24 }}
                      className="glass w-full max-w-sm rounded-3xl p-6 text-center" style={{ boxShadow: "var(--shadow)" }}>
            <AnimatePresence mode="wait">
              {state === "done" ? (
                <motion.div key="done" initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex flex-col items-center">
                  <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: "rgba(52,208,127,0.18)" }}>
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 14 }}>
                      <Check size={34} style={{ color: "var(--green)" }} strokeWidth={3} />
                    </motion.div>
                  </div>
                  <p className="font-display text-lg font-extrabold" style={{ color: "var(--text)" }}>Deleted</p>
                </motion.div>
              ) : (
                <motion.div key="ask" exit={{ opacity: 0 }}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
                       style={{ background: danger ? "rgba(255,92,92,0.15)" : "rgba(245,158,11,0.15)" }}>
                    <AlertTriangle size={30} style={{ color: danger ? "var(--red)" : "var(--accent)" }} />
                  </div>
                  <h3 className="font-display text-xl font-extrabold" style={{ color: "var(--text)" }}>{title}</h3>
                  {description && <p className="mt-2 text-sm text-dim">{description}</p>}
                  <div className="mt-6 flex gap-3">
                    <button onClick={onClose} disabled={state === "loading"} data-testid="confirm-cancel"
                            className="glass pressable flex-1 rounded-2xl py-3 font-bold" style={{ color: "var(--text)" }}>Cancel</button>
                    <button onClick={run} disabled={state === "loading"} data-testid="confirm-ok"
                            className="pressable flex flex-1 items-center justify-center gap-2 rounded-2xl py-3 font-bold text-white"
                            style={{ background: danger ? "var(--red)" : "linear-gradient(135deg,#F59E0B,#D97706)" }}>
                      {state === "loading" ? <Loader2 size={18} className="animate-spin" /> : confirmLabel}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
